/* eslint-disable no-unused-vars */
import { createContext, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "./theme";

// eslint-disable-next-line react-refresh/only-export-components
export const ThemeContext = createContext({
  mode: "light",
  palette: "darkBlue",
  toggleColorMode: () => {},
  setThemePalette: () => {},
});

export function ThemeProvider({ children }) {
  const getInitialTheme = () => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("themeMode") || "light";
    }
    return "light";
  };

  const getInitialPalette = () => {
    if (typeof window !== "undefined") {
      // First check if there's a stored palette preference
      const storedPalette = localStorage.getItem("themePalette");
      if (storedPalette) {
        return storedPalette;
      }

      // Then check if there are stored credentials with a theme preference
      const storedCredentials = localStorage.getItem("credentials");
      if (storedCredentials) {
        try {
          const { themePalette } = JSON.parse(storedCredentials);
          if (themePalette) {
            return themePalette;
          }
        } catch (error) {
          console.error("Error parsing stored credentials:", error);
        }
      }

      // Default to darkBlue if no stored preferences
      return "darkBlue";
    }
    return "darkBlue";
  };

  const [mode, setMode] = useState(getInitialTheme);
  const [palette, setPalette] = useState(getInitialPalette);

  const toggleColorMode = () => {
    setMode((prev) => {
      const newMode = prev === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", newMode);
      return newMode;
    });
  };

  const setThemePalette = (newPalette) => {
    localStorage.setItem("themePalette", newPalette);
    setPalette(newPalette);
  };

  // Persist theme settings
  useEffect(() => {
    localStorage.setItem("themeMode", mode);
    localStorage.setItem("themePalette", palette);
  }, [mode, palette]);

  // Create theme
  const theme = useMemo(() => {
    try {
      return createAppTheme(palette, mode);
    } catch (error) {
      return createAppTheme("darkBlue", "light"); // Fallback theme
    }
  }, [palette, mode]);

  const value = useMemo(
    () => ({
      mode,
      palette,
      toggleColorMode,
      setThemePalette,
    }),
    [mode, palette]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
